import React, { Component } from 'react'

import Quill from 'quill'
import { ClassroomManager } from '@helpers'
import { Note as NT, Files } from '@constants'

import Frame from '../Frame'
import OversizeModal from '../Modals/OversizeModal'
import InvalidTypeModal from '../Modals/InvalidTypeModal'
import Toolbar from './Toolbar'
import Editor from './Editor'
import { useFileUpload } from '@gopeerproject/chuck'

class Note extends Component {
  constructor(props) {
    super(props)

    this.state = {
      tools: [NT.TOOLS.HEADER.NORMAL, NT.TOOLS.ALIGN.LEFT],
      tabs: ClassroomManager.getTabs('note'),
      active: ClassroomManager.getActive('note')
    }
  }

  componentDidMount() {
    ClassroomManager.observe('frames.note.tabs', this.onTabChange)
    ClassroomManager.observe('frames.note.active', this.onActiveChange)
  }

  componentWillUnmount() {
    ClassroomManager.unobserve('frames.note.tabs', this.onTabChange)
    ClassroomManager.unobserve('frames.note.active', this.onActiveChange)
  }

  init = (editor) => {
    ClassroomManager.attachEditor('note', editor)
    editor.on('editor-change', this.onChange)
  }

  loadFile = (file) => {
    ClassroomManager.loadFile({ type: 'note', id: file })
  }

  onTabChange = () => {
    this.setState({ tabs: ClassroomManager.getTabs('note') })
  }

  onActiveChange = () => {
    this.setState({ active: ClassroomManager.getActive('note') })
  }

  onBlur = () => {
    ClassroomManager.getEditor('note').off('editor-change', this.onChange)
  }

  onFocus = () => {
    ClassroomManager.getEditor('note').on('editor-change', this.onChange)
  }

  focus = () => {
    ClassroomManager.getEditor('note').focus()
  }

  onChange = () => {
    const { tools } = this.state

    /** @ref https://github.com/quilljs/quill/blob/develop/core/quill.js#L269 */
    const format = ClassroomManager.getEditor('note').getFormat(
      this.getSelection(true)
    )
    const patch = [
      ...(format.bold ? [NT.TOOLS.BOLD] : []),
      ...(format.italic ? [NT.TOOLS.ITALIC] : []),
      ...(format.strike ? [NT.TOOLS.STRIKE] : []),
      ...(format.underline ? [NT.TOOLS.UNDERLINE] : []),
      ...(format.header && format.header === 1 ? [NT.TOOLS.HEADER.H1] : []),
      ...(format.header && format.header === 3 ? [NT.TOOLS.HEADER.H3] : []),
      ...(!format.header ? [NT.TOOLS.HEADER.NORMAL] : []),
      ...(format.align && format.align === 'center'
        ? [NT.TOOLS.ALIGN.CENTER]
        : []),
      ...(format.align && format.align === 'right'
        ? [NT.TOOLS.ALIGN.RIGHT]
        : []),
      ...(format.align && format.align === 'justify'
        ? [NT.TOOLS.ALIGN.JUSTIFY]
        : []),
      ...(!format.align ? [NT.TOOLS.ALIGN.LEFT] : []),
      ...(format.list && format.list === 'bullet'
        ? [NT.TOOLS.LIST.BULLET]
        : []),
      ...(format.list && format.list === 'ordered'
        ? [NT.TOOLS.LIST.NUMERIC]
        : [])
    ]

    if (
      !patch.reduce(
        (acc, v) => acc && tools.includes(v),
        tools.length === patch.length
      )
    )
      this.setState({ tools: patch })
  }

  upload = (file) => {
    if (!file) return
    if (file.size > Files.MAX_FILE_SIZE) {
      return this.setState({ oversize: [file] })
    } else if (!['image/jpeg', 'image/png'].includes(file.type)) {
      return this.setState({ invalid: [file] })
    }

    this.props
      .upload(
        file,
        {
          type: file.type,
          name: file.name,
          parent: this.state.active
        },
        {
          onProgress: ({ progress }) => {
            this.setState({ progress })
          }
        }
      )
      .then(
        ({ data }) => {
          this.insertImage(data)
          this.setState({ progress: undefined })
        },
        (err) => {
          if (err.data === 'Invalid File Type')
            this.setState({ invalid: [file], progress: undefined })
        }
      )
  }

  insertImage = (url) => {
    ClassroomManager.getEditor('note').insertEmbed(
      this.getSelection().index,
      'image',
      url,
      Quill.sources.USER
    )
  }

  pickFile = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/jpeg, image/png'
    input.onchange = (event) => {
      const [file] = event.target.files
      event.target.value = null

      if (!file) return
      this.upload(file)
    }

    input.click()
  }

  activate = (tool, patch) => {
    const editor = ClassroomManager.getEditor('note')
    if (!editor) return
    const { tools } = this.state
    switch (tool) {
      case NT.TOOLS.IMAGE:
        editor.focus()
        this.pickFile()
        break
      case NT.TOOLS.BOLD:
        editor.format(
          'bold',
          !tools.includes(NT.TOOLS.BOLD),
          Quill.sources.USER
        )
        this.setState({
          tools: tools.includes(NT.TOOLS.BOLD)
            ? tools.filter((v) => v !== NT.TOOLS.BOLD)
            : [...tools, NT.TOOLS.BOLD]
        })
        break
      case NT.TOOLS.ITALIC:
        editor.format(
          'italic',
          !tools.includes(NT.TOOLS.ITALIC),
          Quill.sources.USER
        )
        this.setState({
          tools: tools.includes(NT.TOOLS.ITALIC)
            ? tools.filter((v) => v !== NT.TOOLS.ITALIC)
            : [...tools, NT.TOOLS.ITALIC]
        })
        break
      case NT.TOOLS.UNDERLINE:
        editor.format(
          'underline',
          !tools.includes(NT.TOOLS.UNDERLINE),
          Quill.sources.USER
        )
        this.setState({
          tools: tools.includes(NT.TOOLS.UNDERLINE)
            ? tools.filter((v) => v !== NT.TOOLS.UNDERLINE)
            : [...tools, NT.TOOLS.UNDERLINE]
        })
        break
      case NT.TOOLS.STRIKE:
        editor.format(
          'strike',
          !tools.includes(NT.TOOLS.STRIKE),
          Quill.sources.USER
        )
        this.setState({
          tools: tools.includes(NT.TOOLS.STRIKE)
            ? tools.filter((v) => v !== NT.TOOLS.STRIKE)
            : [...tools, NT.TOOLS.STRIKE]
        })
        break
      case NT.TOOLS.ALIGN:
        editor.format('align', patch, Quill.sources.USER)
        break
      case NT.TOOLS.HEADER:
        editor.format('header', patch, Quill.sources.USER)
        break
      case NT.TOOLS.LIST:
        editor.format('list', patch, Quill.sources.USER)
        break
    }
  }

  getSelection = (focus = false) => {
    const range = ClassroomManager.getEditor('note').getSelection(focus)
    return (
      range ?? {
        index: 0,
        length: 0
      }
    )
  }

  renderToolbar = () => {
    const { state } = this
    const { tools } = state

    return (
      <Toolbar
        tools={tools}
        activate={this.activate}
        close={this.props.close}
        history={{
          undo: () => ClassroomManager.getEditor('note').history.undo(),
          redo: () => ClassroomManager.getEditor('note').history.redo()
        }}
      />
    )
  }

  render = () => {
    const { state } = this

    return (
      <Frame
        className='gp-note'
        component={{ title: 'Note', type: 'note' }}
        toolbar={this.renderToolbar()}
        tabDefaultConfig={NT.CONFIG}
        onCreate={() => ClassroomManager.getEditor('note').focus()}
        tabs={state.tabs}
        active={state.active}
        progress={state.progress}
      >
        <Editor
          init={this.init}
          loadFile={this.loadFile}
          active={this.state.active}
          upload={this.upload}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          focus={this.focus}
        />
        {state.oversize && (
          <OversizeModal
            files={state.oversize}
            close={() => this.setState({ oversize: undefined })}
          />
        )}
        {state.invalid && (
          <InvalidTypeModal
            files={state.invalid}
            close={() => this.setState({ invalid: undefined })}
          />
        )}
      </Frame>
    )
  }
}

const NoteWrapper = (props) => {
  const uploadFile = useFileUpload()
  return <Note {...props} upload={uploadFile} />
}

export default NoteWrapper
